import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { RootLayout } from './RootLayout';
import { ProtectedRoute, PublicRoute, TermsRequiredRoute } from './RouteGuards';

// Eager load critical pages
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import NotFound from '@/pages/NotFound';
import About from '@/pages/About';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';

// Lazy load protected/authenticated pages
const Assessments = lazy(() => import('@/pages/Assessments'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const TakeAssessment = lazy(() => import('@/pages/TakeAssessment'));
const AdminAssessments = lazy(() => import('@/pages/AdminAssessments'));
const ManageUsers = lazy(() => import('@/pages/ManageUsers'));
const Contact = lazy(() => import('@/pages/Contact'));
const Help = lazy(() => import('@/pages/Help'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const UserFeedback = lazy(() => import('@/pages/UserFeedback'));
const ManageCountryPricing = lazy(() => import('@/pages/ManageCountryPricing'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const Results = lazy(() => import('@/pages/Results'));
const Blogs = lazy(() => import('@/pages/Blogs'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const AdminMonitoring = lazy(() => import('@/pages/AdminMonitoring'));
const AcceptTerms = lazy(() => import('@/pages/AcceptTerms'));
const AdminLegalDocuments = lazy(() => import('@/pages/AdminLegalDocuments'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <Landing />,
      },
      {
        path: 'assessments',
        element: <Assessments />,
      },
      {
        path: 'assessment/:id',
        element: <TakeAssessment />,
      },
      {
        path: 'dashboard',
        element: (
          <TermsRequiredRoute>
            <Dashboard />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <TermsRequiredRoute>
            <UserProfile />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'manage-assessments',
        element: (
          <TermsRequiredRoute>
            <AdminAssessments />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <TermsRequiredRoute>
            <ManageUsers />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'admin/country-pricing',
        element: (
          <TermsRequiredRoute>
            <ManageCountryPricing />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'admin/monitoring',
        element: (
          <TermsRequiredRoute>
            <AdminMonitoring />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'admin/legal-documents',
        element: (
          <TermsRequiredRoute>
            <AdminLegalDocuments />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'user-feedback',
        element: (
          <TermsRequiredRoute>
            <UserFeedback />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'terms-of-service',
        element: <TermsOfService />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'help',
        element: (
          <TermsRequiredRoute>
            <Help />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'upgrade',
        element: (
          <TermsRequiredRoute>
            <Upgrade />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'results',
        element: <Results />,
      },
      {
        path: 'blogs',
        element: <Blogs />,
      },
      {
        path: 'blogs/:slug',
        element: <BlogPost />,
      },
      {
        path: 'payment-success',
        element: (
          <TermsRequiredRoute>
            <PaymentSuccess />
          </TermsRequiredRoute>
        ),
      },
      {
        path: 'accept-terms',
        element: (
          <ProtectedRoute>
            <AcceptTerms />
          </ProtectedRoute>
        ),
      },
      {
        path: 'auth',
        element: (
          <PublicRoute>
            <Auth />
          </PublicRoute>
        ),
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);
